/**
 * The gunship's weapons, heard (63.13, DECISIONS 271, 277; the sound sheet sound_sheets/gunship.html): the rules, while
 * `gunshipSound.ts` voices them. In play every round the sim fires is a `gunshipFire` event (core/gunship.ts: the chin's
 * bursts round by round, the side gun's single shots, the swarm's ripple a rocket at a time, the heavy rockets, the
 * pods), so each sound is a one-shot heard where the round leaves the ship (game/actionSound.ts; 69.68: before it, the
 * .50's report for both guns and the LAW's launch for every rocket).
 *   - **The cannons by calibre** (CANNONS): the 20 and 30 mm's chin bursts at their rates (~12 and ~8 a second: every
 *     round heard), and the side gun's 40, 57 and 76 mm, one round at a time. A bigger round is louder, lower and darker,
 *     carries further and rings on longer.
 *   - **The rockets:** the deck swarm's small guided rockets (rippled every 0.08 s, 12 a salvo), the sponsons' heavy
 *     guided ones (the vehicle killers, every 5 s), the pods' unguided ones (every 1.5 s).
 * A sound is heard like a shot (gunfireMix `hearShot`): `gain` against the rifle's report at the gun, halved at `reach`
 * people, the air's cut kept `reach` / 6 further than a rifle's (its low report carries), late by the speed of sound, its
 * echo × `tail`, none past `range` people. Pure: no audio here, no state written. Unplayed guesses (decision 121): the
 * owner's ear decides.
 */
import type { CannonCalibre } from '../types';
import { METRES_PER_PERSON, SPEED_OF_SOUND, type Heard } from './gunfireMix';

/** What the gunship fires, as sounds: a cannon by its calibre, or one of its three rockets. */
export type GunshipSoundKey = CannonCalibre | 'swarm' | 'heavy' | 'pods';
export const GUNSHIP_SOUND_KEYS: readonly GunshipSoundKey[] = [20, 30, 40, 57, 76, 'swarm', 'heavy', 'pods'];

/**
 * Each sound's loudness at the gun (`gain`, the .50's 1.25), its reach (people: the .50's 9.6), its range (people: the
 * .50's 1120) and its echo × (the .50's 1.35). The cannons climb with the round, the 76 mm's report peaking about as high as
 * a shell's blast at 50 people (the sheet's probe); a swarm rocket's launch a little over the LAW's (its motor is
 * small, but twelve come in a second), the heavy rocket's over the .50's.
 */
export const GUNSHIP_HEARD: Record<GunshipSoundKey, { gain: number; reach: number; range: number; tail: number }> = {
  20: { gain: 1.2, reach: 11, range: 1200, tail: 1.3 },
  30: { gain: 1.25, reach: 13, range: 1300, tail: 1.4 },
  40: { gain: 1.3, reach: 16, range: 1500, tail: 1.6 },
  57: { gain: 1.4, reach: 20, range: 1700, tail: 1.8 },
  76: { gain: 1.45, reach: 24, range: 1900, tail: 2 },
  swarm: { gain: 1, reach: 8, range: 900, tail: 0.9 },
  heavy: { gain: 1.2, reach: 14, range: 1400, tail: 1.5 },
  pods: { gain: 1, reach: 10, range: 1100, tail: 1.2 },
};

const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

/** A gunship's sound `people` away on its `pan` side (`env` 0 open ground … 1 deep woods, as `hearShot`); null past its range. */
export function hearGunship(key: GunshipSoundKey, people: number, pan: number, env = 0): Heard | null {
  const k = GUNSHIP_HEARD[key], d = Math.max(0, people);
  if (d > k.range) return null;
  const carry = k.reach / 6;
  return {
    gain: k.gain / (1 + d / k.reach),
    cutoff: clamp(14000 * Math.exp(-d / carry / 220), 700, 14000) * (1 - 0.35 * env),
    delay: (d * METRES_PER_PERSON) / SPEED_OF_SOUND,
    pan: clamp(pan, -1, 1),
    tail: (0.12 + 0.55 * clamp(d / 300, 0, 1) + 0.35 * env) * k.tail,
  };
}

/**
 * How near the guns' mechanism is heard (0 … 1): the chain gun's chug, the side gun's breech slamming shut and its case
 * thrown out are near sounds — half at `MECHANISM_REACH` people, gone in the report by a few times that.
 */
export const MECHANISM_REACH = 12;
export function mechanismNear(people: number): number { const x = Math.max(0, people) / MECHANISM_REACH; return 1 / (1 + x * x); }

/** The sound of a `gunshipFire` event's weapon: the chin's and the side gun's by their calibres, the rockets by kind. */
export function gunshipSoundOf(weapon: 'chin' | 'side' | 'swarm' | 'heavy' | 'pods', calibre: { chin: CannonCalibre; side: CannonCalibre }): GunshipSoundKey {
  return weapon === 'chin' ? calibre.chin : weapon === 'side' ? calibre.side : weapon;
}
